import { useCallback, useEffect, useState } from "react";
import { useAuth } from "../contexts/AuthContext";
import { Loader2, Download, TrendingUp, Users, Clock, Euro, Award, BarChart3 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { KpiSkeleton, CardSkeleton } from "../components/Skeletons";
import PageHeader from "../components/PageHeader";

const CURRENT_YEAR = new Date().getFullYear();

export default function StatsBpfPage() {
  const { axiosAuth } = useAuth();
  const api = axiosAuth();
  const [year, setYear] = useState(CURRENT_YEAR);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/lms/stats/bpf", { params: { year } });
      setData(r.data);
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
    setLoading(false);
  // eslint-disable-next-line
  }, [year]);

  useEffect(() => { load(); }, [load]);

  const exportCsv = () => {
    if (!data) return;
    const lines = [["Session", "Programme", "Debut", "Fin", "Stagiaires", "Heures", "CA HT"]];
    (data.top_sessions || []).forEach((s) => {
      lines.push([s.title, s.program_title, s.start_date, s.end_date, s.stagiaires, s.hours, s.ca_ht]);
    });
    const csv = lines.map((l) => l.map((c) => `"${(c ?? "").toString().replace(/"/g, '""')}"`).join(",")).join("\n");
    const url = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
    const a = document.createElement("a"); a.href = url; a.download = `bpf-${year}.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  if (loading || !data) return (
    <div className="p-6 lg:p-10 space-y-6" data-testid="stats-bpf-loading">
      <KpiSkeleton count={4} />
      <CardSkeleton />
    </div>
  );

  const kpis = [
    { label: "Sessions cloturees", value: data.total_sessions, icon: BarChart3, color: "#3b82f6", sub: `${data.avg_fill_rate_pct}% taux moyen` },
    { label: "Stagiaires formes", value: data.total_stagiaires, icon: Users, color: "#10b981" },
    { label: "Heures dispensees", value: data.total_hours.toLocaleString("fr-FR"), icon: Clock, color: "#f7c935", sub: "h·stagiaire" },
    { label: "CA HT", value: data.total_ca_ht.toLocaleString("fr-FR"), icon: Euro, color: "#f59e0b", sub: "EUR HT" },
    { label: "Satisfaction", value: data.satisfaction?.avg_rating ? `${data.satisfaction.avg_rating}/5` : "—", icon: Award, color: "#8b5cf6", sub: `${data.satisfaction?.responses || 0} reponses` },
    { label: "NPS", value: data.satisfaction?.nps ?? "—", icon: TrendingUp, color: "#06b6d4" },
  ];

  return (
    <div className="p-6 max-w-7xl mx-auto" data-testid="bpf-page">
      <PageHeader
        icon={<BarChart3 />}
        title={`Statistiques BPF ${year}`}
        description="Agrégation annuelle : CA, stagiaires, heures dispensées, taux de satisfaction."
        breadcrumb={[{ label: "Bilan Pédagogique et Financier" }]}
        testId="bpf-header"
        actions={(
          <>
            <select value={year} onChange={(e) => setYear(parseInt(e.target.value))} className="bg-[#0b0811] border border-white/10 text-white rounded px-3 py-2 text-sm" data-testid="bpf-year-select">
              {[CURRENT_YEAR, CURRENT_YEAR - 1, CURRENT_YEAR - 2, CURRENT_YEAR - 3].map((y) => <option key={y} value={y}>{y}</option>)}
            </select>
            <Button onClick={exportCsv} variant="ghost" className="border border-white/10 text-white" data-testid="bpf-export">
              <Download className="w-4 h-4 mr-1" /> Export CSV
            </Button>
          </>
        )}
      />

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-6">
        {kpis.map((k) => {
          const Icon = k.icon;
          return (
            <div key={k.label} className="bg-[#14101c] border border-white/5 rounded-xl p-4" data-testid={"bpf-kpi-" + k.label.split(" ")[0].toLowerCase()}>
              <div className="flex items-center justify-between mb-2">
                <span className="text-[10px] uppercase tracking-wider text-slate-500">{k.label}</span>
                <Icon className="w-4 h-4" style={{ color: k.color }} />
              </div>
              <div className="text-2xl font-black text-white">{k.value}</div>
              {k.sub && <div className="text-[10px] text-slate-500 mt-1">{k.sub}</div>}
            </div>
          );
        })}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
        {/* Financement breakdown */}
        <div className="bg-[#14101c] border border-white/5 rounded-xl p-4">
          <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-3">Repartition par financement</div>
          {(data.by_financement || []).length === 0 ? <div className="text-xs text-slate-600 py-6 text-center">Aucune donnee.</div> : (
            <div className="space-y-2" data-testid="bpf-financement-list">
              {data.by_financement.map((f) => (
                <div key={f.financement} className="bg-[#0b0811] rounded-lg p-2.5 text-xs flex items-center justify-between">
                  <div><span className="font-bold text-white capitalize">{f.financement}</span><span className="text-slate-500 ml-2">{f.stagiaires} stagiaire{f.stagiaires > 1 ? "s" : ""}</span></div>
                  <div className="text-[#f7c935] font-bold">{f.ca_ht.toLocaleString("fr-FR")} EUR</div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Monthly */}
        <div className="bg-[#14101c] border border-white/5 rounded-xl p-4">
          <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-3">Activite mensuelle</div>
          {(data.by_month || []).length === 0 ? <div className="text-xs text-slate-600 py-6 text-center">Aucune donnee.</div> : (
            <div className="space-y-2" data-testid="bpf-monthly-list">
              {data.by_month.map((m) => {
                const maxCa = Math.max(...data.by_month.map((x) => x.ca_ht || 0), 1);
                return (
                  <div key={m.month} className="flex items-center gap-2 text-xs">
                    <span className="w-16 text-slate-400 font-mono">{m.month}</span>
                    <div className="flex-1 bg-white/5 rounded-full h-3 overflow-hidden"><div className="h-full rounded-full" style={{ width: `${Math.max(2, (m.ca_ht / maxCa) * 100)}%`, background: "#f7c935" }} /></div>
                    <span className="w-20 text-right text-[#f7c935] font-bold">{m.ca_ht.toFixed(0)} EUR</span>
                    <span className="w-14 text-right text-slate-500">{m.stagiaires} stag.</span>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Top sessions */}
      <div className="bg-[#14101c] border border-white/5 rounded-xl p-4">
        <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-3">Top sessions (par CA)</div>
        {(data.top_sessions || []).length === 0 ? <div className="text-xs text-slate-600 py-6 text-center">Aucune session cloturee cette annee.</div> : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="bpf-top-sessions">
              <thead className="text-left text-[10px] uppercase text-slate-500"><tr><th className="py-2">Session</th><th className="py-2">Programme</th><th className="py-2">Dates</th><th className="py-2 text-center">Stagiaires</th><th className="py-2 text-center">Heures</th><th className="py-2 text-right">CA HT</th></tr></thead>
              <tbody>
                {data.top_sessions.map((s) => (
                  <tr key={s.session_id} className="border-t border-white/5" data-testid={"bpf-sess-" + s.session_id}>
                    <td className="py-2 text-white font-semibold">{s.title}</td>
                    <td className="py-2 text-slate-400 text-xs">{s.program_title}</td>
                    <td className="py-2 text-slate-500 text-xs">{s.start_date} → {s.end_date}</td>
                    <td className="py-2 text-center text-white">{s.stagiaires}<span className="text-slate-500">/{s.max_participants}</span></td>
                    <td className="py-2 text-center text-slate-300">{s.hours}</td>
                    <td className="py-2 text-right text-[#f7c935] font-bold">{s.ca_ht.toLocaleString("fr-FR")}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
